/* eslint-disable max-lines */
/* eslint-disable react/jsx-sort-props */ /* <-- We disable this because the order of props DOES matter in this file, and we want to be totally in control */
import type {
    ComponentProps,
    MouseEvent,
    ReactElement,
    ReactNode,
} from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import type {
    AutocompleteProps,
    AutocompleteValue,
} from '@mui/material/Autocomplete';
import type { TextFieldProps } from '@mui/material/TextField';

import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import MenuList from '@mui/material/MenuList';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';
import Paper from '@mui/material/Paper';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';

import { InvalidTooltip } from '../InvalidTooltip';

interface NonValueOption {
    nonValueOptionId: number | string;
    label: string;
}

// Props additional to MUI's default props, to customise the BomAutocomplete
interface BomAutocompleteProps<Value, Multiple, DisableClearable, FreeSolo> {
    // All props that should be applied to the inner TextField of Autocomplete
    textfieldProps: TextFieldProps;
    errorMessage?: string;
    renderValue?: (
        value: NonNullable<
            AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>
        >
    ) => ReactNode;
}

const isNonValueOption = (option: unknown): option is NonValueOption =>
    !!option &&
    typeof option === 'object' &&
    'nonValueOptionId' in option &&
    'label' in option;

const BomAutocompletePaper = ({
    children,
    ...paperProps
}: ComponentProps<typeof Paper>): ReactElement => (
    <Paper {...paperProps}>
        <MenuList
            disablePadding
            sx={{ display: 'contents' }}
        >
            {children}
        </MenuList>
    </Paper>
);

/**
 * @description This component is meant to standardize the Autocomplete components within BOM that differ from the Buying Design system.
 * We call this one Raw because the final export should be called BomAutocomplete already. // TODO: Verify if all autocompletes have a invalid tooltip state, then we can do this
 */
const BomAutocomplete = <
    Value,
    Multiple extends boolean | undefined = false,
    DisableClearable extends boolean | undefined = false,
    FreeSolo extends boolean | undefined = false,
>({
    textfieldProps,
    errorMessage,
    renderValue,
    // renderInput,
    ...autocompleteProps
}: BomAutocompleteProps<Value, Multiple, DisableClearable, FreeSolo> &
    /**
     * We omit the renderInput prop here, because otherwise it's required by default.
     * Then we add it back in as an optional prop, with the same type as before.
     * We also omit the variant here, because we want to enforce the variant to be 'outlined'.
     * We use the mui classes for that variant in this component for styling.
     */
    Omit<
        AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>,
        'renderInput' | 'renderValue' | 'variant'
    > & {
        renderInput?: AutocompleteProps<
            Value,
            Multiple,
            DisableClearable,
            FreeSolo
        >['renderInput'];
    }): ReactElement => {
    // MUI v9 fires a focus then blur event on mount before any user interaction.
    // Children's effects (MUI's) run before parent effects (ours), so hasMounted.current
    // is still false when the spurious events fire — preventing RHF from marking the field as touched.
    const hasMounted = useRef(false);

    const { palette, typography, spacing } = useTheme();

    const [hasFocus, setHasFocus] = useState(false);
    const [hasHover, setHasHover] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    const showValue = !!renderValue && !hasFocus && !!autocompleteProps.value;

    const outlineWidth = isOpen || hasFocus ? 2 : 1;

    const outlineColor = useMemo(() => {
        if (errorMessage) return palette.error.main;
        if ((isOpen || hasFocus || hasHover) && !autocompleteProps.disabled) {
            return palette.primary.main;
        }
        return 'transparent';
    }, [
        autocompleteProps.disabled,
        errorMessage,
        hasHover,
        hasFocus,
        isOpen,
        palette.error.main,
        palette.primary.main,
    ]);

    useEffect(() => {
        hasMounted.current = true;
    }, []);

    return (
        <InvalidTooltip
            title={errorMessage}
            open={!hasFocus && hasHover && !!errorMessage && !isOpen}
        >
            <Autocomplete<Value, Multiple, DisableClearable, FreeSolo>
                /**
                 * DEFAULT | The following are default props, and can be overwritten by {...autocompleteProps}
                 */
                fullWidth
                disableClearable={false as DisableClearable}
                /**
                 * OVERWRITES | Below line applies the props that are passed into this component to overwrite default props
                 */
                {...autocompleteProps}
                /**
                 * RESTRICTED | From here on down we overwrite the {...autocompleteProps} again, thus preventing overwrites for those props.
                 * All props that take an object have to be placed below the {...autocompleteProps}
                 * This is because we don't want to overwrite the prop, but the contents inside it's object value.
                 * Inside those objects, the same rules apply. Before is default, spread is overwrites, and everything after is restricted.
                 */
                getOptionLabel={(option) => {
                    // Handle NonValueOption first (like the "add material on the fly" button)
                    if (isNonValueOption(option)) {
                        return ''; // Return empty string instead of option.label
                    }

                    const originalGetOptionLabel =
                        autocompleteProps.getOptionLabel;

                    let label: string;

                    if (originalGetOptionLabel) {
                        label = originalGetOptionLabel(option);
                    } else {
                        // Fallback to default behavior
                        label =
                            typeof option === 'string'
                                ? option
                                : String(option);
                    }

                    return label;
                }}
                sx={{
                    height: 1,
                    ...(autocompleteProps.sx as object | undefined),
                }}
                // TODO: Check in BIT if these props can be deleted or not
                slots={{
                    paper: BomAutocompletePaper,
                    ...autocompleteProps.slots,
                }}
                slotProps={{
                    clearIndicator: { title: '' }, // Remove clearIndicator's tooltip
                    popupIndicator: { title: '' }, // Remove popupIndicator's tooltip
                    paper: {
                        // BOM differs from Buying Design System, so we need to overwrite some styles
                        sx: {
                            [`& .${autocompleteClasses.option}`]: {
                                ...typography['captions.default'],
                            },
                        },
                    },
                    ...autocompleteProps.slotProps,
                }}
                /**
                 * This calls and returns the renderInput prop if it is provided.
                 * If not, it uses the default renderInput logic.
                 */
                renderInput={(params) => (
                    <Box
                        onClick={(e: MouseEvent<HTMLDivElement>): void => {
                            if (!autocompleteProps.disabled) {
                                e.preventDefault();
                                setHasFocus(true);
                            }
                        }}
                        sx={{
                            position: 'relative',
                            height: 1,
                            width: 1,
                            cursor: 'pointer',
                        }}
                    >
                        {renderValue &&
                            autocompleteProps.value &&
                            !isNonValueOption(autocompleteProps.value) && (
                                <Box
                                    sx={{
                                        position: 'relative',
                                        zIndex: 1,
                                        height: 1,
                                        width: 1,
                                        opacity: showValue ? 1 : 0,
                                        pointerEvents: 'none',
                                        transition: 'opacity 0.2s ease-in-out',
                                    }}
                                >
                                    {renderValue(autocompleteProps.value)}
                                </Box>
                            )}
                        <TextField
                            focused={hasFocus}
                            variant="outlined" // Set default variant to outlined
                            {...params}
                            {...textfieldProps}
                            fullWidth
                            placeholder={
                                isNonValueOption(autocompleteProps.value)
                                    ? ''
                                    : textfieldProps.placeholder
                            }
                            // BOM differs from Buying Design System, so we need to overwrite some styles
                            sx={{
                                height: 1,
                                top: 0,
                                position: 'absolute',

                                ...(textfieldProps.sx as object | undefined),

                                [`& .${autocompleteClasses.inputRoot}`]: {
                                    height: 1,
                                    /**
                                     * This padding right is to ensure distance with the clear and popup indicators.
                                     * The distance is 4px, but when there is no clear icon, we need to subtract 16 + 4 = 20 pixels.
                                     * 16px is the width of the clear icon.
                                     * 4px is the distance between both indicators.
                                     */
                                    paddingRight: `${autocompleteProps.disableClearable || !autocompleteProps.value ? 32 : 52}px !important`,
                                    ['& input']: {
                                        ...typography['captions.default'],
                                        opacity: `${showValue ? 0 : 1}  !important`,
                                    },
                                },

                                [`& .${outlinedInputClasses.root}`]: {
                                    backgroundColor: 'transparent',
                                    [`& .${outlinedInputClasses.notchedOutline}`]:
                                        {
                                            borderColor:
                                                'transparent !important',
                                            outlineStyle: 'solid',
                                            outlineWidth,
                                            outlineColor: outlineColor,
                                            padding: spacing(0, 3),
                                            zIndex: 500,
                                        },
                                },

                                [`& .${autocompleteClasses.popupIndicator}`]: {
                                    ['& svg']: {
                                        width: 16,
                                        height: 16,
                                    },
                                },

                                [`& .${autocompleteClasses.clearIndicator}`]: {
                                    margin: '4px !important',
                                    ['& svg']: {
                                        width: 16,
                                        height: 16,
                                    },
                                },
                            }}
                            slotProps={{
                                ...params.slotProps,

                                input: {
                                    ...params.slotProps.input,
                                    ...(textfieldProps.slotProps
                                        ?.input as Record<string, unknown>),
                                },
                            }}
                        />
                    </Box>
                )}
                onOpen={(...args): void => {
                    setIsOpen(true);
                    autocompleteProps.onOpen?.(...args);
                }}
                onClose={(...args): void => {
                    setIsOpen(false);
                    autocompleteProps.onClose?.(...args);
                }}
                onFocus={(...args): void => {
                    setHasFocus(true);
                    autocompleteProps.onFocus?.(...args);
                }}
                onBlur={(...args): void => {
                    setHasFocus(false);
                    if (hasMounted.current) {
                        autocompleteProps.onBlur?.(...args);
                    }
                }}
                onMouseEnter={(...args) => {
                    setHasHover(true);
                    autocompleteProps.onMouseEnter?.(...args);
                }}
                onMouseLeave={(...args) => {
                    setHasHover(false);
                    autocompleteProps.onMouseLeave?.(...args);
                }}
            />
        </InvalidTooltip>
    );
};

export { BomAutocomplete, isNonValueOption };
